import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import type {
  Session,
  User,
  AuthError,
  AuthResponse as SupabaseAuthResponse,
} from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';

// Define return types for auth actions to be consistent
interface AuthResponse {
  data?: SupabaseAuthResponse['data'];
  error: AuthError | { message: string } | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  isConfigured: boolean;
  signInWithGoogle: () => Promise<AuthResponse>;
  signInWithApple: () => Promise<AuthResponse>;
  signInWithPassword: (email: string, pass: string) => Promise<AuthResponse>;
  signUp: (email: string, pass: string) => Promise<AuthResponse>;
  signOut: () => Promise<AuthResponse>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Safety check: if supabase client failed to init, stop loading immediately
    if (!supabase || !isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    // 1. Get initial session
    supabase.auth
      .getSession()
      .then(({ data: { session: sess } }) => {
        setSession(sess);
        setUser(sess?.user ?? null);
        setLoading(false);
      })
      .catch((err: unknown) => {
        console.error('Auth Session Error:', err);
        setLoading(false);
      });

    // 2. Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      setUser(sess?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const getRedirectTo = () => `${window.location.origin}/auth/callback`;

  // Helper for missing config error
  const configError = (): AuthResponse => ({
    error: {
      message: 'Supabase not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.',
    },
  });

  const signInWithGoogle = async (): Promise<AuthResponse> => {
    if (!supabase) return configError();
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: getRedirectTo() },
    });
    return { error };
  };

  const signInWithApple = async (): Promise<AuthResponse> => {
    if (!supabase) return configError();
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo: getRedirectTo() },
    });
    return { error };
  };

  const signInWithPassword = async (email: string, password: string): Promise<AuthResponse> => {
    if (!supabase) return configError();
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  };

  const signUp = async (email: string, password: string): Promise<AuthResponse> => {
    if (!supabase) return configError();
    const { data, error } = await supabase.auth.signUp({ email, password });
    return { data, error };
  };

  const signOut = async (): Promise<AuthResponse> => {
    if (!supabase) return configError();
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        loading,
        isConfigured: isSupabaseConfigured,
        signInWithGoogle,
        signInWithApple,
        signInWithPassword,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
